import { convertLeadToOrder, getLeadById } from 'application/ports/TransactionService';
import { parse } from 'query-string';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { StoreObject as OnboardingInfoType } from '../../../frameworks_drivers/redux/reducers/onboardingInformation';
import { setAllFromAPIResponse } from '../../../frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { paths } from '../../../frameworks_drivers/routes';
import store, { useAppStore } from '../../../store';
import { TrackerUtil } from '../../../tracking/TrackerUtil';
import { useAlertUtil } from '../../../utils/AlertUtil';
import { useTrackGoogleEvents } from '../../../interface_adapters/hooks/GoogleTagContext';

interface ConfirmOfferState {
  isLoadingInitialData: boolean;
  apiCallInProgress: boolean;
  leadId: string;
  orderConfirmationKey: string;
  readKey: string;
  isExpired: boolean;
  onboardingInfo?: OnboardingInfoType;
  convertLeadToOrder: () => void;
  offerId: string;
}

const StateContext = createContext<ConfirmOfferState>({} as ConfirmOfferState);

export const ConfirmOfferStateProvider: React.FC = ({ children }) => {
  const { trackSubmitOrder } = useTrackGoogleEvents();

  const location = useLocation();
  const navigate = useNavigate();
  const appStore = useAppStore();

  const { t } = useTranslation();
  const { showAlert } = useAlertUtil();

  const [apiCallInProgress, setApiCallInProgress] = useState(false);
  const [leadId, setLeadId] = useState('');
  const [orderConfirmationKey, setOrderConfirmationKey] = useState('');
  const [readKey, setReadKey] = useState('');
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [offerId, setOfferId] = useState<string>('');

  const onboardingInfo = store.getState().onboardingInfo;

  const convert = () => {
    TrackerUtil.trackBtnClickByIdentifier('order-now-after-lead');
    setApiCallInProgress(true);

    const dpaVersion = appStore.getState().misc.dataProcessingAgreement?.version || '';

    convertLeadToOrder(leadId!, orderConfirmationKey!, dpaVersion)
      .then((response: any) => {
        setApiCallInProgress(false);
        const newRoute = paths.End;

        navigate(`${newRoute}?id=${leadId}&rkey=${readKey}`);
      })
      .catch((error: any) => {
        showAlert('pageSummaryAlertConfirmingOrderErrorText', {
          code: error.response?.status,
          message: error.message
        });

        TrackerUtil.trackError('api-order-now-after-lead-server-error', error.message);
      })
      .finally(() => {
        setApiCallInProgress(false);
      });

    trackSubmitOrder();
  };

  const value = {
    isLoadingInitialData,
    apiCallInProgress,
    leadId,
    orderConfirmationKey,
    readKey,
    isExpired,
    onboardingInfo,
    convertLeadToOrder: convert,
    offerId
  };

  useEffect(() => {
    const urlParameters = parse(location.search);
    if (!urlParameters.id) {
      showAlert('pageSummaryAlertErrorIdMissingText', {
        error: '910 Missing id'
      });
    } else if (!urlParameters.rkey || !urlParameters.okey) {
      showAlert('pageSummaryAlertErrorIdMissingText', {
        error: '911 Missing key'
      });
    } else {
      getLeadById(urlParameters.id! as string, urlParameters.rkey! as string)
        .then((response: any) => {
          setOfferId(response.data?.id);

          if (response.data.status === 'expired') {
            setIsExpired(true);
          }

          store.dispatch(setAllFromAPIResponse(response.data));

          setApiCallInProgress(false);
          setLeadId(urlParameters.id! as string);
          setOrderConfirmationKey(urlParameters.okey! as string);
          setReadKey(urlParameters.rkey! as string);
          setIsLoadingInitialData(false);
        })
        .catch((error: any) => {
          const errorMessage =
            error.response?.status === 401
              ? '401 invalid offer id'
              : error.response?.status === 404
              ? '404 Invalid key'
              : 'Unknown Error';

          showAlert('pageSummaryAlertLoadingDataErrorText', {
            error: errorMessage
          });
          console.error('Error getting lead data!');
          console.error(error);
        });
    }
  }, [showAlert, location.search, t]);

  return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
};

export const useConfirmOfferState = () => useContext(StateContext);
